import { Popover, Typography } from "antd-v5";
import Vocabulary from "components/Vocabulary";
import dayjs from "dayjs";
import _, { isEmpty } from "lodash";
import React from "react";
import { formatNumber } from "utils/numberUtil";

import {
  DEFAULT_DURATION,
  RISK_RATING_LEVELS,
  SEVERITY_LEVELS,
  SLA_PHASES,
  SLA_START_OPTIONS,
  SLA_START_OPTION_LABELS,
} from "./constants";
import type {
  PhaseDuration,
  RiskRatingDurations,
  SLAFormTemplate,
  SLAPhase,
  SLAPolicyFormState,
  SLATemplate,
  SeverityDurations,
} from "./types";

const { Text } = Typography;
const createDefaultPhaseDuration = (): PhaseDuration => ({
  triageDuration: DEFAULT_DURATION,
  remediationDuration: DEFAULT_DURATION,
  resolutionDuration: DEFAULT_DURATION,
});

export const getInitialFormState = (
  isEdit: boolean,
  policyData: SLAFormTemplate | null,
): SLAPolicyFormState => {
  if (isEdit) {
    // Transform policyData to form state
    const severityDurations: SeverityDurations = {} as SeverityDurations;
    const riskRatingDurations: RiskRatingDurations = {} as RiskRatingDurations;

    (policyData!.slaConfigurations || []).forEach((config) => {
      const duration: PhaseDuration = {
        triageDuration: renderSLATime(config.triageDuration),
        remediationDuration: renderSLATime(config.remediationDuration),
        resolutionDuration: renderSLATime(config.resolutionDuration),
      };

      if (policyData!.templateType === "SEVERITY") {
        severityDurations[config.level as keyof SeverityDurations] = duration;
      } else {
        riskRatingDurations[config.level as keyof RiskRatingDurations] =
          duration;
      }
    });

    // Extract phase-specific form fields
    const triagePhase = (policyData!.enabledPhases || []).find(
      (p) => p.phase === SLA_PHASES.TRIAGE,
    );
    const remediationPhase = (policyData!.enabledPhases || []).find(
      (p) => p.phase === SLA_PHASES.REMEDIATION,
    );

    return {
      name: policyData!.name,
      description: policyData!.description,
      templateType: policyData!.templateType,
      isDefault: policyData!.isDefault,
      enabledPhases: (policyData!.enabledPhases || []).map((p) => p.phase),
      triageStartDate: triagePhase?.startDate,
      remediationStartDate: remediationPhase?.startDate,
      slaStartConfig: policyData!.slaStartConfig,
      severityDurations,
      riskRatingDurations,
    };
  }

  // Default state for create mode
  const severityDurations: SeverityDurations = {} as SeverityDurations;
  SEVERITY_LEVELS.forEach((level) => {
    severityDurations[level] = createDefaultPhaseDuration();
  });

  const riskRatingDurations: RiskRatingDurations = {} as RiskRatingDurations;
  RISK_RATING_LEVELS.forEach((level) => {
    riskRatingDurations[level] = createDefaultPhaseDuration();
  });

  return {
    name: "",
    description: "",
    templateType: "SEVERITY",
    isDefault: false,
    enabledPhases: [
      SLA_PHASES.TRIAGE,
      SLA_PHASES.REMEDIATION,
      SLA_PHASES.RESOLUTION,
    ],
    triageStartDate: SLA_START_OPTIONS.FOUND_ON_DATE,
    remediationStartDate: SLA_START_OPTIONS.CONFIRMED_TRANSITION_DATE,
    severityDurations,
    riskRatingDurations,
  };
};

const createDurationConfig = (
  duration: PhaseDuration | undefined,
  enabledPhases: SLAPhase[],
): {
  triageDuration: number | null;
  remediationDuration: number | null;
  resolutionDuration: number | null;
} => ({
  triageDuration:
    enabledPhases.includes(SLA_PHASES.TRIAGE) && duration?.triageDuration
      ? getSLATimeInMs(duration.triageDuration)
      : null,
  remediationDuration:
    enabledPhases.includes(SLA_PHASES.REMEDIATION) &&
    duration?.remediationDuration
      ? getSLATimeInMs(duration.remediationDuration)
      : null,
  resolutionDuration:
    enabledPhases.includes(SLA_PHASES.RESOLUTION) &&
    duration?.resolutionDuration
      ? getSLATimeInMs(duration.resolutionDuration)
      : null,
});

export const transformFormStateToAPIPayload = (
  formState: SLAPolicyFormState,
) => {
  const enabledPhases = formState.enabledPhases || [];

  // Build severity or risk rating configs
  const configs: Record<string, ReturnType<typeof createDurationConfig>> = {};

  if (formState.templateType === "SEVERITY") {
    const severityDurations = formState.severityDurations || {};
    SEVERITY_LEVELS.forEach((level) => {
      configs[level] = createDurationConfig(
        severityDurations[level],
        enabledPhases,
      );
    });
  } else {
    const riskRatingDurations = formState.riskRatingDurations || {};
    RISK_RATING_LEVELS.forEach((level) => {
      configs[level] = createDurationConfig(
        riskRatingDurations[level],
        enabledPhases,
      );
    });
  }

  const payload: any = {
    name: formState.name.trim(),
    description: formState.description.trim(),
    templateType: formState.templateType,
    triageSlaStartOption: formState.triageStartDate,
    remediationSlaStartOption: formState.remediationStartDate,
    enabledPhases,
    ...(formState.templateType === "SEVERITY"
      ? { severityConfigs: configs }
      : { riskRatingConfigs: configs }),
  };

  // Add slaStartConfig if it exists in form state
  if (formState.slaStartConfig?.tagKey) {
    payload.slaStartConfig = formState.slaStartConfig;
  }

  return payload;
};

export const TAG_KEY_TOOLTIP_CONTENT = (
  <>
    Supported Tag Value Formats:
    <ul>
      <li>MM/DD/YYYY (e.g., 01/15/2024)</li>
      <li>MM-DD-YYYY (e.g., 01-15-2024)</li>
      <li>MM/DD/YYYY HH:MM:SS AM/PM (e.g., 01/15/2024 02:30:45 PM)</li>
      <li>MM/DD/YYYY HH:MM:SS (e.g., 01/15/2024 14:30:45)</li>
      Triage SLA calculation will default to the 'Found On' date if supported
      tag value formats are not provided
    </ul>
  </>
);

export const renderSLATime = (time: number | null) =>
  time !== null ? dayjs.duration(time).asDays() : null;

export const getSLATimeInMs = (day: number) =>
  dayjs.duration(day, "days").asMilliseconds();

export const renderSLAProductSubproductWithPopover = (
  template: Pick<
    SLATemplate,
    "isDefault" | "productCount" | "subProductCount" | "configs"
  >,
) => {
  const { product, subproduct } = template.configs || {};
  const productList = !isEmpty(product)
    ? renderPopOverContent(product)
    : undefined;
  const subProductList = !isEmpty(subproduct)
    ? renderPopOverContent(subproduct)
    : undefined;
  const productDetail = (
    <Text type="secondary" style={{ fontSize: 13 }}>
      <Vocabulary name="product" />:{" "}
      {template.productCount
        ? formatNumber(template.productCount, "long")
        : 0}{" "}
    </Text>
  );
  const subProductDetail = (
    <Text type="secondary" style={{ fontSize: 13 }}>
      <Vocabulary name="product" />: <Vocabulary name="subProduct" />:{" "}
      {template.subProductCount
        ? formatNumber(template.subProductCount, "long")
        : 0}
    </Text>
  );
  return (
    <>
      (
      {productList && !template.isDefault ? (
        <Popover content={productList} trigger={["hover"]}>
          {productDetail}
        </Popover>
      ) : (
        productDetail
      )}
      {subProductList && !template.isDefault ? (
        <Popover content={subProductList} trigger={["hover"]}>
          {subProductDetail}
        </Popover>
      ) : (
        subProductDetail
      )}
      )
    </>
  );
};

export const renderPopOverContent = (
  data: Array<{ id: number; name: string }>,
) => (
  <div style={{ maxHeight: 150, maxWidth: 200, overflow: "auto" }}>
    {_.map(data, (sla, i) => (
      <span key={sla?.name} className="m-r-xs m-b-xs">
        {sla.name}
        {i !== data.length - 1 && ","}
      </span>
    ))}
  </div>
);

export const SLA_RESOLVED_FINDING_STATUS_TOOLTIP_CONTENT = (
  <>
    Applies this SLA to all unresolved findings. Excludes findings marked as{" "}
    <Vocabulary name="suppressed" />, <Vocabulary name="falsePositive" />,{" "}
    <Vocabulary name="acceptRisk" />, <Vocabulary name="controlledStatus" /> and{" "}
    <Vocabulary name="mitigated" />.
  </>
);

export const REMEDIATION_DROPDOWN_OPTIONS = [
  {
    label: (
      <>
        <Vocabulary name="inProgressStatus" />/<Vocabulary name="confirmed" />{" "}
        Status Transition Date
      </>
    ),
    value: SLA_START_OPTIONS.CONFIRMED_TRANSITION_DATE,
  },
  {
    label: SLA_START_OPTION_LABELS.TICKET_CREATION_DATE,
    value: SLA_START_OPTIONS.TICKET_CREATION_DATE,
  },
];
